import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { useSelector } from "react-redux";
import { useFormReducer } from "@tphglobal/common/hooks";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Button,
  Form,
  FormError,
  FormRow,
  FormRowItem,
  MaterialAutocompleteInput,
  OutlinedContainer,
  TextInput,
  TimeInput,
} from "@tphglobal/components";
import { brand, greyScaleColour } from "@tphglobal/common/theme/style.palette";
import { FixedTitle, mapFlagToValue } from "@tphglobal/common/utils";
import { fontSize } from "@tphglobal/common/theme/style.typography";
import CustomCheckbox from "@tphglobal/components/checkBoxInput";
import moment from "moment";
import {
  StyledAccordionSummary,
  StyledAccordionDetails,
  StyledAccordion,
} from "./styles";
import {
  CompanyDetailedEntity,
  CustomField,
  DailyTime,
  DropListItem,
  Payas,
  SetLockedRows,
  TimeOption,
  UnitsByRangeDate,
  Validators,
} from "../../models";
import { required } from "../../utils";
import CustomFieldComponent from "./customFieldComponent";
import {
  formatTime,
  joinDelimitedValues,
  parseDelimitedValues,
  toCamelCase,
} from "../../utils/commonFunctions";
import { ReduxState } from "../../redux/reducers";
import messages from "../../messages";
import timeInputField from "./tableInputs/timeInputField";
import MultiSelectInput from "./tableInputs/multiSelectInput";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DEFAULT_UNIT_FIELDS } from "../../utils/constant";
import { getValidSelectedLocations } from "../../utils/timeCardFunctions";

interface FormData {
  StartMeal: Date;
  StartMeal2: Date;
  StartMeal3: Date;
  FinishMeal1: Date;
  FinishMeal3: Date;
  FinishMeal2: Date;
  MB1ND: boolean;
  MB2ND: boolean;
  MB3ND: boolean;
  Start: Date;
  Finish: Date;
  Payas: { id: string; label: string };
  Travel1: string;
  Note: string | null;
  Travel2: string;
  TotalMBDed: string;
  Unit: string | null;
  Locations: string | null;
  CamCall: Date;
  CamWrap: Date;
}

const expandIconStyles = {
  height: fontSize?.h3,
  width: fontSize?.h3,
  fill: brand?.secondaryMain,
};

interface PassedData {
  data: DailyTime;
  rowIndex: number;
}

interface Props {
  onCancel: () => void;
  onSuccess: (data: DailyTime) => void;
  passedData: PassedData;
  companyDetails?: CompanyDetailedEntity;
  yesterdayTimings?: DailyTime;
  setYesterdayTimings?: (yesterdayTimings: DailyTime | null) => void;
  setMealBreakLockedRows?: (mealBreakLockedRows: any) => any;
  rowIndex?: number;
  locationsForUnit: DropListItem[];
  unitListWithEmpty: DropListItem[];
  unitsByRange: UnitsByRangeDate[];
  date: string;
  selectedLocations: string | null;
  payAsLockedRows: number[];
  setPayAsLockedRows?: SetLockedRows;
}

let validators: Validators = {
  Payas: [required(messages?.general?.validations?.payAsRequired)],
  Note: [required(messages?.general?.validations?.noteRequired)],
};

const UpdateTimeForm: React.FC<Props> = ({
  onCancel,
  onSuccess,
  passedData,
  companyDetails,
  yesterdayTimings,
  setYesterdayTimings,
  setMealBreakLockedRows,
  rowIndex,
  locationsForUnit,
  unitListWithEmpty,
  unitsByRange,
  date,
  selectedLocations,
  payAsLockedRows,
  setPayAsLockedRows,
}) => {
  const isFirstRun = useRef(true);
  const data: DailyTime = passedData?.data;
  const userProfile = useSelector((state: ReduxState) => state?.profile);
  const [submitError, setSubmitError] = useState<string>("");
  const [useMBValues, setUseMBValues] = useState<boolean>(false);
  const [locations, setLocations] = useState<DropListItem[]>([]);

  const isLocked = companyDetails.allowMealBreak || false;
  const startMB2Visible = !companyDetails?.StartMB2Hide || false;
  const startMB3Visible = !companyDetails?.StartMB3Hide || false;

  const { handleSubmit, connectField, change, formValues } =
    useFormReducer(validators);

  const getTotalHours = () => {
    if (formValues?.Payas?.value?.id === "DNW") {
      return "00:00";
    }
    const startMB1 = moment(formValues?.StartMeal?.value, "HH:mm");
    const startMB2 = moment(formValues?.StartMeal2?.value, "HH:mm");
    const startMB3 = moment(formValues?.StartMeal3?.value, "HH:mm");
    const finishMB1 = moment(formValues?.FinishMeal1?.value, "HH:mm");
    const finishMB2 = moment(formValues?.FinishMeal2?.value, "HH:mm");
    const finishMB3 = moment(formValues?.FinishMeal3?.value, "HH:mm");

    const startWorkTime = moment(formValues?.Start?.value, "HH:mm");
    const finishWorkTime = moment(formValues?.Finish?.value, "HH:mm");

    if (finishWorkTime.isBefore(startWorkTime)) {
      finishWorkTime.add(1, "day");
    }
    const travelToTime = moment(formValues?.Travel1?.value, "HH:mm");
    const travelFromTime = moment(formValues?.Travel2?.value, "HH:mm");
    const defaultMealBreakTime = moment(formValues?.TotalMBDed?.value, "HH:mm");

    const calculateMealBreakDuration = () => {
      const totalMealBreakDuration = moment.duration(0);

      // Clone moments to avoid mutating originals
      const startMB1Copy = startMB1.clone();
      const finishMB1Copy = finishMB1.clone();
      const startMB2Copy = startMB2.clone();
      const finishMB2Copy = finishMB2.clone();
      const startMB3Copy = startMB3.clone();
      const finishMB3Copy = finishMB3.clone();

      // Handle midnight crossover for each meal break
      if (
        finishMB1Copy.isValid() &&
        startMB1Copy.isValid() &&
        finishMB1Copy.isBefore(startMB1Copy)
      ) {
        finishMB1Copy.add(1, "day");
      }
      if (
        finishMB2Copy.isValid() &&
        startMB2Copy.isValid() &&
        finishMB2Copy.isBefore(startMB2Copy)
      ) {
        finishMB2Copy.add(1, "day");
      }
      if (
        finishMB3Copy.isValid() &&
        startMB3Copy.isValid() &&
        finishMB3Copy.isBefore(startMB3Copy)
      ) {
        finishMB3Copy.add(1, "day");
      }

      // Add meal break durations using the corrected times
      if (
        startMB1Copy.isValid() &&
        finishMB1Copy.isValid() &&
        finishMB1Copy.isAfter(startMB1Copy)
      ) {
        totalMealBreakDuration.add(
          moment.duration(finishMB1Copy.diff(startMB1Copy)),
        );
      }
      if (
        startMB2Copy.isValid() &&
        finishMB2Copy.isValid() &&
        finishMB2Copy.isAfter(startMB2Copy)
      ) {
        totalMealBreakDuration.add(
          moment.duration(finishMB2Copy.diff(startMB2Copy)),
        );
      }
      if (
        startMB3Copy.isValid() &&
        finishMB3Copy.isValid() &&
        finishMB3Copy.isAfter(startMB3Copy)
      ) {
        totalMealBreakDuration.add(
          moment.duration(finishMB3Copy.diff(startMB3Copy)),
        );
      }

      // Subtract non-deductible meal breaks
      if (totalMealBreakDuration.asMilliseconds() > 0) {
        if (formValues?.MB1ND?.value) {
          totalMealBreakDuration.subtract(
            moment.duration(finishMB1Copy.diff(startMB1Copy)),
          );
        }
        if (formValues?.MB2ND?.value) {
          totalMealBreakDuration.subtract(
            moment.duration(finishMB2Copy.diff(startMB2Copy)),
          );
        }
        if (formValues?.MB3ND?.value) {
          totalMealBreakDuration.subtract(
            moment.duration(finishMB3Copy.diff(startMB3Copy)),
          );
        }

        return totalMealBreakDuration;
      }
      return moment.duration(
        defaultMealBreakTime.diff(moment().startOf("day")),
      );
    };

    const mealBreakDuration = calculateMealBreakDuration();

    if (startWorkTime.isValid() && finishWorkTime.isValid()) {
      if (finishWorkTime.isBefore(startWorkTime)) {
        return "00:00";
      }

      const totalWorkDuration = moment.duration(
        finishWorkTime.diff(startWorkTime),
      );

      let netDuration = moment.duration(0);

      if (
        mealBreakDuration.asMilliseconds() < totalWorkDuration.asMilliseconds()
      ) {
        netDuration = totalWorkDuration.subtract(mealBreakDuration);
      }

      if (!companyDetails?.TravelToHide) {
        const travelToDuration = moment.duration(
          travelToTime.diff(moment().startOf("day")),
        );
        netDuration = netDuration.add(travelToDuration);
      }

      if (!companyDetails?.TravelFromHide) {
        const travelFromDuration = moment.duration(
          travelFromTime.diff(moment().startOf("day")),
        );
        netDuration = netDuration.add(travelFromDuration);
      }

      const netDurationMoment = moment.duration(netDuration.asMilliseconds());
      const totalHours = `${Math.floor(netDurationMoment.asHours())
        .toString()
        .padStart(2, "0")}:${netDurationMoment
        .minutes()
        .toString()
        .padStart(2, "0")}`;

      return totalHours;
    }

    return "00:00";
  };

  const onSubmit = async (data: FormData) => {
    const startMB1 = moment(data?.StartMeal || "00:00", "HH:mm");
    const startMB2 = moment(data?.StartMeal2 || "00:00", "HH:mm");
    const startMB3 = moment(data?.StartMeal3 || "00:00", "HH:mm");
    const finishMB1 = moment(data?.FinishMeal1 || "00:00", "HH:mm");
    const finishMB2 = moment(data?.FinishMeal2 || "00:00", "HH:mm");
    const finishMB3 = moment(data?.FinishMeal3 || "00:00", "HH:mm");
    const defaultMealBreakTime = moment(data?.TotalMBDed || "00:00", "HH:mm");

    const mealBreakTime = moment.duration(
      defaultMealBreakTime.diff(moment().startOf("day")),
    );
    const startWorkTime = moment(data?.Start || "00:00", "HH:mm");
    const finishWorkTime = moment(data?.Finish || "00:00", "HH:mm");
    const travelToTime = moment(data?.Travel1 || "00:00", "HH:mm");
    const travelFromTime = moment(data?.Travel2 || "00:00", "HH:mm");
    const totalHours = getTotalHours();

    if (
      startWorkTime?.isValid() &&
      finishWorkTime?.isValid() &&
      mealBreakTime?.isValid()
    ) {
      if (finishWorkTime.isBefore(startWorkTime)) {
        finishWorkTime.add(1, "day");
      }

      const totalWorkDuration = moment.duration(
        finishWorkTime.diff(startWorkTime),
      );

      if (
        moment.duration(totalHours).asMinutes() >
        moment.duration(24, "hours").asMinutes()
      ) {
        setSubmitError("Your total daily hours cannot exceed 24 hours");
        return;
      }

      if (mealBreakTime.asMinutes() > totalWorkDuration.asMinutes()) {
        setSubmitError(
          "Meal break cannot be longer than the total work duration",
        );
        return;
      }
    } else {
      if (!startWorkTime?.isValid()) {
        setSubmitError("Invalid start work time");
      } else if (!finishWorkTime?.isValid()) {
        setSubmitError("Invalid finish work time");
      } else if (!mealBreakTime?.isValid()) {
        setSubmitError("Invalid meal break time");
      } else if (!travelToTime?.isValid() && companyDetails?.TravelToHide) {
        setSubmitError("Invalid travel to time");
      } else if (!travelFromTime?.isValid() && companyDetails?.TravelFromHide) {
        setSubmitError("Invalid travel from time");
      } else if (!startMB1?.isValid() || !finishMB1?.isValid()) {
        setSubmitError("Invalid start or finish meal break 1 time");
      } else if (!startMB2?.isValid() || !finishMB2?.isValid()) {
        setSubmitError("Invalid start or finish meal break 2 time");
      } else if (!startMB3?.isValid() || !finishMB3?.isValid()) {
        setSubmitError("Invalid start or finish meal break 3 time");
      }
      return;
    }
    setSubmitError("");

    const updatedDayData: DailyTime = { ...passedData?.data };

    Object.keys(data).forEach((key: keyof FormData) => {
      if (key === "Unit") {
        const selectedUnit =
          typeof data[key] === "object"
            ? typeof data[key]?.value === "object"
              ? data[key]?.value?.value
              : data[key]?.value
            : data[key];
        updatedDayData[key] = selectedUnit ?? null;
      } else if (key === "Locations") {
        updatedDayData[key] = joinDelimitedValues(data[key]) || null;
      } else if (key === "Note") {
        updatedDayData[key] = data[key];
      } else if (key === "Payas") {
        updatedDayData[key] = data[key]?.label;
      } else if (key === "MB1ND" || key === "MB2ND" || key === "MB3ND") {
        updatedDayData[key] = data[key] ? "1" : "0";
      } else {
        updatedDayData[key] = moment(data[key]).format("HH:mm");
      }
    });
    updatedDayData.CustomTimeFields = (
      updatedDayData?.CustomTimeFields || []
    ).map((timeField: CustomField) => {
      const key = toCamelCase(timeField?.Prompt);

      if (key in data) {
        const value = (data as { [key: string]: any })[key];

        return {
          ...timeField,
          Value:
            typeof value === "boolean"
              ? value
                ? "1"
                : "0"
              : typeof value === "object" && value !== null && "label" in value
                ? value.label
                : value,
        };
      }

      return timeField;
    });

    updatedDayData.customNote = `${data?.Payas?.label || data?.Payas} ${
      data?.Note || ""
    }`;
    updatedDayData.TotalHours = getTotalHours();
    if (updatedDayData?.Unit && setPayAsLockedRows) {
      setPayAsLockedRows((prev) => {
        const updated = [...prev];
        updated[rowIndex || 0] = 1;
        return updated;
      });
    }
    onSuccess(updatedDayData);
  };
  const setFormValues = () => {
    change(
      "Payas",
      data?.Payas !== "0" && {
        id: companyDetails?.Payas?.find(
          (opt: Payas) => opt?.Value === data?.Payas,
        )?.Flag,
        label: data?.Payas,
      },
    );
    change("Start", moment(data?.Start, "HH:mm"));
    change("TotalMBDed", moment(data?.TotalMBDed, "HH:mm"));
    change("Finish", moment(data?.Finish, "HH:mm"));
    change("StartMeal", moment(data?.StartMeal, "HH:mm"));
    change("StartMeal2", moment(data?.StartMeal2, "HH:mm"));
    change("StartMeal3", moment(data?.StartMeal3, "HH:mm"));
    change("FinishMeal1", moment(data?.FinishMeal1, "HH:mm"));
    change("FinishMeal2", moment(data?.FinishMeal2, "HH:mm"));
    change("FinishMeal3", moment(data?.FinishMeal3, "HH:mm"));
    change("Travel1", moment(data?.Travel1, "HH:mm"));
    change("Travel2", moment(data?.Travel2, "HH:mm"));
    change("MB1ND", data?.MB1ND === "1");
    change("MB2ND", data?.MB2ND === "1");
    change("MB3ND", data?.MB3ND === "1");

    let userNote = data?.customNote || "";
    const payAsNote = data?.Payas || "";

    if (userNote && payAsNote && userNote.startsWith(payAsNote)) {
      userNote = userNote.slice(payAsNote.length).trimStart();
    }
    change("Note", userNote);

    if (data?.Unit && setPayAsLockedRows) {
      setPayAsLockedRows((prev) => {
        const updated = [...prev];
        updated[rowIndex || 0] = data?.Payas !== "0" ? 1 : 0;
        return updated;
      });
    }

    if (passedData?.data?.CustomTimeFields?.length > 0) {
      passedData.data.CustomTimeFields.forEach((field: CustomField) => {
        const value = field?.Value;

        change(
          toCamelCase(field?.Prompt),
          value === "1" ? true : value === "0" ? false : value,
        );
      });
    }

    const newValidators: Validators = { ...validators };
    passedData?.data?.CustomTimeFields?.forEach((field: CustomField) => {
      if (field?.required === 1) {
        newValidators[toCamelCase(field?.Prompt)] = [
          required("Field cannot be empty, please enter some value."),
        ];
      }
    });
    validators = newValidators;
  };
  useEffect(() => {
    setFormValues();
    change("Unit", data?.Unit || null);
    change("CamCall", moment(data?.CamCall || "00:00", "HH:mm"));
    change("CamWrap", moment(data?.CamWrap || "00:00", "HH:mm"));
    change("Locations", parseDelimitedValues(data?.Locations));
    setLocations(locationsForUnit);
  }, []);

  useEffect(() => {
    if (yesterdayTimings) {
      const copyMode = userProfile.CopyYesterdayTimes;
      const isWorked = yesterdayTimings?.Payas?.toLowerCase() === "worked";
      let payAsValue = null;
      if (isWorked) {
        change("Payas", {
          id: companyDetails?.Payas?.find(
            (opt: Payas) => opt?.Value === "Worked",
          )?.Flag,
          label: "Worked",
        });
        payAsValue = "Worked";
      } else {
        // reset PayAs when yesterday PayAs is NOT Worked
        change("Payas", null);
        setPayAsLockedRows?.((prev) => {
          const updated = [...prev];
          updated[rowIndex || 0] = 0;
          return updated;
        });
      }

      change("Start", moment(yesterdayTimings?.Start, "HH:mm"));
      change("TotalMBDed", moment(yesterdayTimings?.TotalMBDed, "HH:mm"));
      change("Finish", moment(yesterdayTimings?.Finish, "HH:mm"));
      change("StartMeal", moment(yesterdayTimings?.StartMeal, "HH:mm"));
      change("StartMeal2", moment(yesterdayTimings?.StartMeal2, "HH:mm"));
      change("StartMeal3", moment(yesterdayTimings?.StartMeal3, "HH:mm"));
      change("FinishMeal1", moment(yesterdayTimings?.FinishMeal1, "HH:mm"));
      change("FinishMeal2", moment(yesterdayTimings?.FinishMeal2, "HH:mm"));
      change("FinishMeal3", moment(yesterdayTimings?.FinishMeal3, "HH:mm"));
      change("Travel1", moment(yesterdayTimings?.Travel1, "HH:mm"));
      change("Travel2", moment(yesterdayTimings?.Travel2, "HH:mm"));
      change("MB1ND", yesterdayTimings?.MB1ND === "1");
      change("MB2ND", yesterdayTimings?.MB2ND === "1");
      change("MB3ND", yesterdayTimings?.MB3ND === "1");
      change("MB3ND", yesterdayTimings?.MB3ND === "1");

      if (copyMode === "COPY_EVERYTHING") {
        if (payAsValue) {
          let userNote = yesterdayTimings?.customNote || "";

          if (userNote && payAsValue && userNote.startsWith(payAsValue)) {
            userNote = userNote.slice(payAsValue.length).trimStart();
          }
          change("Note", userNote);
        } else {
          change("Note", "");
        }
        // Copy CUSTOM FIELDS
        if (yesterdayTimings?.CustomTimeFields?.length > 0) {
          yesterdayTimings.CustomTimeFields.forEach((field: CustomField) => {
            const key = toCamelCase(field.Prompt);
            change(
              key,
              field.Value === "1"
                ? true
                : field.Value === "0"
                  ? false
                  : field.Value,
            );
          });
        }
      } else {
        change("Note", "");
      }
      setYesterdayTimings(null);
    }
  }, [yesterdayTimings]);

  useEffect(() => {
    getTotalHours();
  }, [formValues?.Start, formValues?.Finish, formValues?.TotalMBDed]);

  useEffect(() => {
    const hasValidMealTime = [
      formValues?.StartMeal?.value,
      formValues?.StartMeal2?.value,
      formValues?.StartMeal3?.value,
      formValues?.FinishMeal1?.value,
      formValues?.FinishMeal2?.value,
      formValues?.FinishMeal3?.value,
    ].some((value) => value && moment(value).format("HH:mm") !== "00:00");

    if (hasValidMealTime) {
      const startMoment1 = moment(formValues?.StartMeal?.value);
      const finishMoment1 = moment(formValues?.FinishMeal1?.value);
      const startMoment2 = moment(formValues?.StartMeal2?.value);
      const finishMoment2 = moment(formValues?.FinishMeal2?.value);
      const startMoment3 = moment(formValues?.StartMeal3?.value);
      const finishMoment3 = moment(formValues?.FinishMeal3?.value);

      // Handle midnight crossover for each meal break
      if (
        finishMoment1.isValid() &&
        startMoment1.isValid() &&
        finishMoment1.isBefore(startMoment1)
      ) {
        finishMoment1.add(1, "day");
      }
      if (
        finishMoment2.isValid() &&
        startMoment2.isValid() &&
        finishMoment2.isBefore(startMoment2)
      ) {
        finishMoment2.add(1, "day");
      }
      if (
        finishMoment3.isValid() &&
        startMoment3.isValid() &&
        finishMoment3.isBefore(startMoment3)
      ) {
        finishMoment3.add(1, "day");
      }

      const diff1 = finishMoment1.diff(startMoment1);
      const diff2 = finishMoment2.diff(startMoment2);
      const diff3 = finishMoment3.diff(startMoment3);

      const totalMealTimeMili: number = [diff1, diff2, diff3].reduce(
        (total, diff) => (diff > 0 ? total + diff : total),
        0,
      );
      const formattedMealTime: string = formatTime(totalMealTimeMili);

      if (totalMealTimeMili >= 0) {
        change("TotalMBDed", moment(formattedMealTime, "HH:mm"));
        setUseMBValues(totalMealTimeMili !== 0);
        setMealBreakLockedRows((prevRows: any) => {
          const updatedRows = [...prevRows];
          updatedRows[rowIndex] = 1;
          return updatedRows;
        });
      }
    } else {
      setUseMBValues(false);
      // change("TotalMBDed", moment("00:00", "HH:mm"));
      setMealBreakLockedRows((prevRows: any) => {
        const updatedRows = [...prevRows];
        updatedRows[rowIndex] = 0;
        return updatedRows;
      });
    }
  }, [
    formValues?.StartMeal?.value,
    formValues?.StartMeal2?.value,
    formValues?.StartMeal3?.value,
    formValues?.FinishMeal1?.value,
    formValues?.FinishMeal2?.value,
    formValues?.FinishMeal3?.value,
  ]);

  useEffect(() => {
    const isDNW = formValues?.Payas?.value?.id === "DNW";
    if (isDNW) {
      const zeroTime = moment("00:00", "HH:mm");

      // Work & travel times
      change("Start", zeroTime);
      change("Finish", zeroTime);
      change("Travel1", zeroTime);
      change("Travel2", zeroTime);
      change("TotalMBDed", zeroTime);
    }
  }, [formValues?.Payas]);

  useEffect(() => {
    const selectedUnit =
      typeof formValues?.Unit?.value === "object"
        ? formValues?.Unit?.value?.value
        : formValues?.Unit?.value;
    if (selectedUnit) {
      const foundDate = unitsByRange?.find((u) => u.date === date);
      const foundUnit = foundDate?.units?.find((u) => u.unit === selectedUnit);
      const locations =
        foundUnit?.locations?.map((loc, i) => ({
          value: loc,
          label: loc,
          id: i,
        })) || [];
      setLocations(locations);

      const validSelectedLocations = getValidSelectedLocations(
        selectedLocations,
        locations,
      );

      change(
        "Locations",
        validSelectedLocations.length > 0
          ? validSelectedLocations
          : locations.length === 1
            ? [locations[0].value]
            : [],
      );

      change("CamCall", moment(foundUnit?.CamCall || "00:00", "HH:mm"));
      change("CamWrap", moment(foundUnit?.CamWrap || "00:00", "HH:mm"));

      if (isFirstRun.current) {
        setFormValues();
        isFirstRun.current = false;
        return;
      }

      if (foundUnit) {
        change(
          "Payas",
          foundUnit.Payas !== "0"
            ? {
                id: companyDetails?.Payas?.find(
                  (opt: Payas) => opt?.Value === foundUnit.Payas,
                )?.Flag,
                label: foundUnit.Payas,
              }
            : null,
        );
        change("Start", moment(foundUnit.Start, "HH:mm"));
        change("TotalMBDed", moment(foundUnit.TotalMBDed, "HH:mm"));
        change("Finish", moment(foundUnit.Finish, "HH:mm"));
        change("StartMeal", moment(foundUnit.StartMeal, "HH:mm"));
        change("StartMeal2", moment(foundUnit.StartMeal2, "HH:mm"));
        change("StartMeal3", moment(foundUnit.StartMeal3, "HH:mm"));
        change("FinishMeal1", moment(foundUnit.FinishMeal1, "HH:mm"));
        change("FinishMeal2", moment(foundUnit.FinishMeal2, "HH:mm"));
        change("FinishMeal3", moment(foundUnit.FinishMeal3, "HH:mm"));
        change("Travel1", moment(foundUnit.Travel1, "HH:mm"));
        change("Travel2", moment(foundUnit.Travel2, "HH:mm"));
        change("MB1ND", foundUnit.MB1ND === "1");
        change("MB2ND", foundUnit.MB2ND === "1");
        change("MB3ND", foundUnit.MB3ND === "1");

        let userNote = foundUnit.customNote || "";
        const payAsNote = foundUnit.Payas || "";

        if (userNote && payAsNote && userNote.startsWith(payAsNote)) {
          userNote = userNote.slice(payAsNote.length).trimStart();
        }
        change("Note", userNote);
        setPayAsLockedRows?.((prev) => {
          const updated = [...prev];
          updated[rowIndex || 0] = foundUnit.Payas !== "0" ? 1 : 0;
          return updated;
        });
      }
    } else {
      setLocations([]);
      change("Locations", []);
      if (showUnitSection) {
        if (isFirstRun.current && typeof formValues?.Unit === "object") {
          setFormValues();
          isFirstRun.current = false;
          return;
        }
        change("Payas", null);
        change("Start", moment(DEFAULT_UNIT_FIELDS.Start, "HH:mm"));
        change("TotalMBDed", moment(DEFAULT_UNIT_FIELDS.TotalMBDed, "HH:mm"));
        change("Finish", moment(DEFAULT_UNIT_FIELDS.Finish, "HH:mm"));
        change("StartMeal", moment(DEFAULT_UNIT_FIELDS.StartMeal, "HH:mm"));
        change("StartMeal2", moment(DEFAULT_UNIT_FIELDS.StartMeal2, "HH:mm"));
        change("StartMeal3", moment(DEFAULT_UNIT_FIELDS.StartMeal3, "HH:mm"));
        change("FinishMeal1", moment(DEFAULT_UNIT_FIELDS.FinishMeal1, "HH:mm"));
        change("FinishMeal2", moment(DEFAULT_UNIT_FIELDS.FinishMeal2, "HH:mm"));
        change("FinishMeal3", moment(DEFAULT_UNIT_FIELDS.FinishMeal3, "HH:mm"));
        change("Travel1", moment(DEFAULT_UNIT_FIELDS.Travel1, "HH:mm"));
        change("Travel2", moment(DEFAULT_UNIT_FIELDS.Travel2, "HH:mm"));
        change("MB1ND", DEFAULT_UNIT_FIELDS.MB1ND === "1");
        change("MB2ND", DEFAULT_UNIT_FIELDS.MB2ND === "1");
        change("MB3ND", DEFAULT_UNIT_FIELDS.MB3ND === "1");
        change("CamCall", moment(DEFAULT_UNIT_FIELDS.CamCall, "HH:mm"));
        change("CamWrap", moment(DEFAULT_UNIT_FIELDS.CamWrap, "HH:mm"));
        change("Note", DEFAULT_UNIT_FIELDS.Note);
        setPayAsLockedRows?.((prev) => {
          const updated = [...prev];
          updated[rowIndex || 0] = 0;
          return updated;
        });
      }
    }
  }, [formValues?.Unit]);

  const userMinutesStep = userProfile?.MinutsStep || 5;
  const showCustomTimeField =
    userProfile?.TimeOption === TimeOption.Type ? timeInputField : undefined;
  const isDropdownDisabled = userProfile?.TimeOption === TimeOption.Type;
  const isDNW = formValues?.Payas?.value?.id === "DNW";
  const showUnitSection =
    unitsByRange?.length > 0 && unitListWithEmpty?.length > 0;
  const camCall = formValues?.CamCall?.value?.format?.("HH:mm");
  const camWrap = formValues?.CamWrap?.value?.format?.("HH:mm");
  const showCamCall = Boolean(
    formValues?.Unit?.value && camCall && camCall !== "00:00",
  );
  const showCamWrap = Boolean(
    formValues?.Unit?.value && camWrap && camWrap !== "00:00",
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Grid container padding="0 24px">
        {showUnitSection && (
          <FormRow marginBottom="12px" columnSpacing={2}>
            <FormRowItem>
              {connectField("Unit", {
                label: "Unit",
                options: unitListWithEmpty,
              })(MaterialAutocompleteInput)}
            </FormRowItem>
            <FormRowItem>
              {connectField("Locations", {
                fieldKey: "Locations",
                rowIndex: passedData?.rowIndex,
                list: locations,
                enablePortal: formValues?.Unit?.value && locations.length > 0,
                onChange: (vals) => change("Locations", vals),
                IconComponent: KeyboardArrowDownIcon,
                value: formValues?.Locations?.value,
                label: "Locations",
              })(MultiSelectInput)}
            </FormRowItem>
          </FormRow>
        )}
        {showUnitSection && (showCamCall || showCamWrap) ? (
          <FormRow marginBottom="12px" columnSpacing={2}>
            {showCamCall ? (
              <FormRowItem>
                {connectField("CamCall", {
                  label: messages?.timeCard?.updateTime?.camCall,
                  disabled: true,
                  CustomTimeField: showCustomTimeField,
                  minuteSteps: userMinutesStep,
                  disableDropDown: true,
                })(TimeInput)}
              </FormRowItem>
            ) : (
              <></>
            )}
            {showCamWrap ? (
              <FormRowItem>
                {connectField("CamWrap", {
                  label: messages?.timeCard?.updateTime?.camWrap,
                  disabled: true,
                  CustomTimeField: showCustomTimeField,
                  minuteSteps: userMinutesStep,
                  disableDropDown: true,
                })(TimeInput)}
              </FormRowItem>
            ) : (
              <></>
            )}
          </FormRow>
        ) : (
          <></>
        )}
        <FormRow marginBottom="12px" columnSpacing={2} minWidth="868px">
          <FormRowItem>
            {connectField("Payas", {
              label: messages?.timeCard?.updateTime?.payAs,
              options: companyDetails?.Payas?.map(mapFlagToValue),
              disabled: payAsLockedRows[passedData?.rowIndex || 0] === 1,
            })(MaterialAutocompleteInput)}
          </FormRowItem>
          {!companyDetails?.TravelToHide && (
            <FormRowItem>
              {connectField("Travel1", {
                label: messages?.timeCard?.updateTime?.travelTo,
                disabled: isDNW,
                CustomTimeField: showCustomTimeField,
                minuteSteps: userMinutesStep,
                disableDropDown: isDropdownDisabled,
              })(TimeInput)}
            </FormRowItem>
          )}
          {!companyDetails?.TravelFromHide && (
            <FormRowItem>
              {connectField("Travel2", {
                label: messages?.timeCard?.updateTime?.travelFrom,
                disabled: isDNW,
                CustomTimeField: showCustomTimeField,
                minuteSteps: userMinutesStep,
                disableDropDown: isDropdownDisabled,
              })(TimeInput)}
            </FormRowItem>
          )}
        </FormRow>
        <FormRow marginBottom="4px" columnSpacing={2}>
          <FormRowItem>
            {connectField("Start", {
              label: messages?.timeCard?.updateTime?.startWork,
              disabled: isDNW,
              CustomTimeField: showCustomTimeField,
              minuteSteps: userMinutesStep,
              disableDropDown: isDropdownDisabled,
            })(TimeInput)}
          </FormRowItem>
          <FormRowItem>
            {connectField("TotalMBDed", {
              label: messages?.timeCard?.updateTime?.mealBreak,
              disabled: isDNW || useMBValues || isLocked,
              CustomTimeField: showCustomTimeField,
              minuteSteps: userMinutesStep,
              disableDropDown: isDropdownDisabled,
            })(TimeInput)}
          </FormRowItem>
          <FormRowItem>
            {connectField("Finish", {
              label: messages?.timeCard?.updateTime?.finishWork,
              disabled: isDNW,
              CustomTimeField: showCustomTimeField,
              minuteSteps: userMinutesStep,
              disableDropDown: isDropdownDisabled,
            })(TimeInput)}
          </FormRowItem>
        </FormRow>
        <FormRow display="flex" justifyContent="center">
          {formValues?.Payas?.value?.id !== "DNW" && (
            <StyledAccordion>
              <StyledAccordionSummary
                flexGrow="0"
                expandIcon={<ExpandMoreIcon style={expandIconStyles} />}
                aria-controls="australia"
                id="australia"
              >
                <FixedTitle
                  varient="Fixed14Medium"
                  color={brand?.secondaryMain}
                >
                  {messages?.timeCard?.updateTime?.mbDetails}
                </FixedTitle>
              </StyledAccordionSummary>

              <StyledAccordionDetails>
                <OutlinedContainer
                  borderRadius="8px"
                  padding="16px"
                  borderColor={greyScaleColour?.grey70}
                >
                  <FormRow>
                    <FixedTitle
                      varient="Fixed14Regular"
                      color={brand?.secondaryMain}
                    >
                      {messages?.timeCard?.updateTime?.mealBreak}
                    </FixedTitle>
                  </FormRow>
                  <FormRow xs={12} spacing={2}>
                    <FormRowItem>
                      {connectField("StartMeal", {
                        label: `${messages?.timeCard?.updateTime?.startMB}1`,
                        CustomTimeField: showCustomTimeField,
                        minuteSteps: userMinutesStep,
                        disableDropDown: isDropdownDisabled,
                      })(TimeInput)}
                    </FormRowItem>
                    <FormRowItem>
                      {connectField("FinishMeal1", {
                        label: `${messages?.timeCard?.updateTime?.finishMB}1`,
                        CustomTimeField: showCustomTimeField,
                        minuteSteps: userMinutesStep,
                        disableDropDown: isDropdownDisabled,
                      })(TimeInput)}
                    </FormRowItem>
                    {!companyDetails?.NonDeductibleMB1Hide && (
                      <FormRowItem display="flex" alignSelf="center">
                        {connectField("MB1ND", {
                          label: messages?.timeCard?.updateTime?.nonDed1,
                          flexDirection: "row-reverse",
                        })(CustomCheckbox)}
                      </FormRowItem>
                    )}
                  </FormRow>
                  {startMB2Visible && (
                    <FormRow xs={12} spacing={2}>
                      <FormRowItem>
                        {connectField("StartMeal2", {
                          label: `${messages?.timeCard?.updateTime?.startMB}2`,
                          CustomTimeField: showCustomTimeField,
                          minuteSteps: userMinutesStep,
                          disableDropDown: isDropdownDisabled,
                        })(TimeInput)}
                      </FormRowItem>
                      <FormRowItem>
                        {connectField("FinishMeal2", {
                          label: `${messages?.timeCard?.updateTime?.finishMB}2`,
                          CustomTimeField: showCustomTimeField,
                          minuteSteps: userMinutesStep,
                          disableDropDown: isDropdownDisabled,
                        })(TimeInput)}
                      </FormRowItem>
                      {!companyDetails?.NonDeductibleMB2Hide && (
                        <FormRowItem display="flex" alignSelf="center">
                          {connectField("MB2ND", {
                            label: messages?.timeCard?.updateTime?.nonDed2,
                            flexDirection: "row-reverse",
                          })(CustomCheckbox)}
                        </FormRowItem>
                      )}
                    </FormRow>
                  )}
                  {startMB3Visible && (
                    <FormRow xs={12} spacing={2} marginBottom={0}>
                      <FormRowItem>
                        {connectField("StartMeal3", {
                          label: `${messages?.timeCard?.updateTime?.startMB}3`,
                          CustomTimeField: showCustomTimeField,
                          minuteSteps: userMinutesStep,
                          disableDropDown: isDropdownDisabled,
                        })(TimeInput)}
                      </FormRowItem>
                      <FormRowItem>
                        {connectField("FinishMeal3", {
                          label: `${messages?.timeCard?.updateTime?.finishMB}3`,
                          CustomTimeField: showCustomTimeField,
                          minuteSteps: userMinutesStep,
                          disableDropDown: isDropdownDisabled,
                        })(TimeInput)}
                      </FormRowItem>
                      {!companyDetails?.NonDeductibleMB3Hide && (
                        <FormRowItem display="flex" alignSelf="center">
                          {connectField("MB3ND", {
                            label: messages?.timeCard?.updateTime?.nonDed3,
                            flexDirection: "row-reverse",
                          })(CustomCheckbox)}
                        </FormRowItem>
                      )}
                    </FormRow>
                  )}
                </OutlinedContainer>
              </StyledAccordionDetails>
            </StyledAccordion>
          )}
        </FormRow>

        <FormRow marginTop="4px">
          <FixedTitle varient="Fixed14Bold">
            {messages?.timeCard?.updateTime?.totalHours}
          </FixedTitle>
          <FixedTitle varient="Fixed14Bold">{getTotalHours()}</FixedTitle>
        </FormRow>

        <FormRow>
          {connectField("Note", {
            label: messages?.timeCard?.updateTime?.note,
            multiline: true,
            rows: 3,
            autoHeight: true,
          })(TextInput)}
        </FormRow>

        <FormRow xs={12} spacing={2} marginBottom={0}>
          {passedData?.data?.CustomTimeFields?.map((field: CustomField) => {
            return Boolean(field?.hide) ? null : (
              <FormRowItem xs={4}>
                <CustomFieldComponent
                  field={field}
                  connectField={connectField}
                />
              </FormRowItem>
            );
          })}
        </FormRow>
        {submitError && (
          <FormRow marginTop={2}>
            <FormRowItem>
              <FormError message={submitError} />
            </FormRowItem>
          </FormRow>
        )}
      </Grid>
      <OutlinedContainer
        noBorder
        borderRadius="0"
        padding="14px 24px"
        styles={{ backgroundColor: greyScaleColour?.grey60, marginTop: "24px" }}
      >
        <Grid container justifyContent="flex-end">
          <Grid item>
            <Grid container spacing={2}>
              <Grid item>
                <Button
                  variant="outlined"
                  buttonColor={brand?.black}
                  color="primary"
                  size="large"
                  onClick={onCancel}
                  label={messages?.general?.cancel}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  type="submit"
                  label={messages?.general?.update}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </OutlinedContainer>
    </Form>
  );
};

export default UpdateTimeForm;
